import type { HomeData, ProjectSummary } from "@minga/protocol"
import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { lazy, type ReactNode, Suspense, useMemo, useState } from "react"
import { SetupStrip } from "@/components/setup-strip"
import { ErrorText } from "@/components/ui/alert"
import { Avatar } from "@/components/ui/avatar"
import { Chip } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Sheet, SheetHeader } from "@/components/ui/sheet"
import { PageTitle, WorkspaceShell } from "@/components/workspace-shell"
import { setupViewerOf } from "@/features/project/setup-viewer"
import { parseManifest } from "@/features/runners/manifest"
import { platformQuery } from "@/features/runners/platform-api"
import { runnersQuery } from "@/features/runners/runner-api"
import { subscriptionsQuery } from "@/features/subscriptions/api"
import { choiceIn } from "@/features/subscriptions/model"
import { greeting } from "@/features/workspaces/workspace-model"
import { readFlag, writeFlag } from "@/lib/last-workspace"
import { connectedAppsQuery } from "@/lib/oauth"
import { pushStatusQuery } from "@/lib/push"
import { useInstallState } from "@/lib/pwa"
import { sessionQuery } from "@/lib/queries"
import { homeQuery, membersQuery } from "@/lib/settings-api"
import { useBootMark } from "@/lib/telemetry"
import { useTitleBadge } from "@/lib/title-badge"
import { useNarrow } from "@/lib/use-media-query"
import { cn } from "@/lib/utils"
import { agentNameQuery, toRunnersV1 } from "@/lib/workspace-api"
import { newId } from "@/model/commands"
import { WORDS, whenText } from "@/model/glossary"
import { blocksLead } from "@/model/setup"
import { workspaceRoute } from "@/router"
import { BriefView } from "./brief-view"
import {
  checklist,
  checklistProgress,
  countsLine,
  goallessLine,
  goalProgress,
  type HomeReachFacts,
  homeBriefs,
  homeNeedsCount,
  homeReach,
  homeSetup,
  leadLine,
  leadWhere,
  pairedRunnerReady,
  projectProblems,
  showStartCard,
  sortProjects,
  stepsAfterSetup,
  subLine,
  subscriptionCounts,
  withPlatformAwake,
} from "./model"

// Opened from a button, so not on Home's boot path: they load with their sheet.
const ConnectPanel = lazy(() =>
  import("@/features/connect/connect-panel").then((m) => ({ default: m.ConnectPanel })),
)
const NewProjectForm = lazy(() =>
  import("@/features/projects/new-project").then((m) => ({ default: m.NewProjectForm })),
)

const setupKey = (ws: string) => `minga:setupHidden:${ws}`

/**
 * Decision 0168: what Home reads each project's reach from, as the project page does (the runner list with what others
 * shared with the viewer, the Platform Runner, the viewer's choice of subscriptions), so a project card and the strip
 * say "can't work" exactly when the chat does. Null while loading.
 */
const useHomeReach = (workspaceId: string, viewerId: string): HomeReachFacts | null => {
  const rows = useQuery(runnersQuery(workspaceId)).data
  const platformRead = useQuery({ ...platformQuery(workspaceId), refetchInterval: false })
  const overview = useQuery(subscriptionsQuery(workspaceId)).data
  const platform = platformRead.data ?? (platformRead.isError ? null : undefined)
  return useMemo(
    () =>
      rows && platform !== undefined
        ? {
            viewerId,
            runners: toRunnersV1(rows),
            platform,
            chosenIn: (projectId: string) => (overview ? choiceIn(overview, projectId) : null),
          }
        : null,
    [rows, platform, overview, viewerId],
  )
}

/** The workspace home (settings wave §3.4): Needs you, projects, runners, and the setup checklist. */
export function HomeDashboard() {
  const { workspace } = workspaceRoute.useRouteContext()
  const session = useQuery(sessionQuery).data
  const home = useQuery(homeQuery(workspace.id))
  useBootMark("home-data", home.data !== undefined)
  // `?new=project` (from Pair a runner, opened from the New project sheet) opens the sheet again.
  const reopen = useRouterState({
    select: (s) => (s.location.search as Record<string, unknown>).new === "project",
  })
  const [creating, setCreating] = useState(reopen)
  const [connecting, setConnecting] = useState(false)
  const [setupHidden, setSetupHidden] = useState(() => readFlag(setupKey(workspace.id)))
  const narrow = useNarrow()
  const navigate = useNavigate()
  // re-walk 2026-10-28 (0166 §4): one command id per New project sheet, made here so the lead-name suggestion for it is
  // fetched before the sheet opens (the name field was empty for a moment); a new one after each close (a reused id
  // would replay the last create)
  const [sheetCommandId, setSheetCommandId] = useState(() => newId("cmd"))
  useQuery(agentNameQuery(workspace.id, { seed: sheetCommandId }))
  const openCreate = (open: boolean) => {
    setCreating(open)
    if (!open) setSheetCommandId(newId("cmd"))
    // Closing drops `?new=project`, so a reload doesn't open the sheet again.
    if (!open && reopen)
      void navigate({ to: "/w/$workspaceId", params: { workspaceId: workspace.id }, replace: true })
  }
  // re-walk 2026-10-28: the Platform Runner with a subscription counts as online in the sub-line (it starts on demand)
  const awakeRunners = withPlatformAwake(
    useQuery(runnersQuery(workspace.id)).data,
    useQuery({ ...platformQuery(workspace.id), refetchInterval: false }).data,
  )
  useTitleBadge(`${workspace.name} · Minga`, home.data ? homeNeedsCount(home.data) : 0)
  const hideSetup = (hidden: boolean) => {
    writeFlag(setupKey(workspace.id), hidden)
    setSetupHidden(hidden)
  }

  return (
    <WorkspaceShell>
      <PageTitle title={greeting(session?.user.name, new Date().getHours())}>
        <div className="flex flex-wrap items-center gap-2">
          {/* onboarding week A4: a member's Home doesn't lead with starting something else */}
          <Button
            size="md"
            variant={workspace.role === "member" ? "secondary" : undefined}
            onClick={() => setCreating(true)}
          >
            ＋ New project
          </Button>
        </div>
      </PageTitle>
      {/* UX pass phase 2 (P6, P13): no ··· menu (its rows are in the workspace picker and Settings) and no Needs-you
          block (the header's Needs you lists what waits); Show setup sits here when the checklist is hidden */}
      <p className="-mt-4 mb-6 flex flex-wrap items-baseline gap-x-3 text-sm text-muted">
        <span>
          {home.data ? subLine(home.data, { role: workspace.role, runners: awakeRunners }) : workspace.name}
        </span>
        {setupHidden && (
          <button type="button" className="tap link text-sm" onClick={() => hideSetup(false)}>
            Show setup
          </button>
        )}
      </p>

      {home.data && !setupHidden && (
        <Checklist
          home={home.data}
          onHide={() => hideSetup(true)}
          onNewProject={() => setCreating(true)}
          onConnect={() => setConnecting(true)}
        />
      )}
      {home.data && <HomeStrip home={home.data} checklistShown={!setupHidden} />}

      {/* decision 0098: what each lead told you last, above the projects */}
      {home.data && <Briefs home={home.data} />}

      {/* UX pass phase 2 (P12): runners live in the header pill and Settings › Runners, not a card here */}
      <ProjectsWidget home={home} onNewProject={() => setCreating(true)} />

      <Sheet
        open={creating}
        onOpenChange={openCreate}
        title="New project"
        side={narrow ? "bottom" : "right"}
        className={narrow ? "h-[92dvh] max-h-[92dvh]" : "w-[min(560px,100vw)]"}
      >
        <Suspense fallback={null}>
          <NewProjectForm
            key={sheetCommandId}
            commandId={sheetCommandId}
            workspaceId={workspace.id}
            role={workspace.role}
            onClose={() => openCreate(false)}
          />
        </Suspense>
      </Sheet>
      <Sheet
        open={connecting}
        onOpenChange={setConnecting}
        title="Connect Claude Code or Codex"
        side={narrow ? "bottom" : "right"}
        className={narrow ? "h-[92dvh] max-h-[92dvh]" : "w-[min(560px,100vw)]"}
      >
        {connecting && (
          <div className="flex h-full flex-col">
            <SheetHeader title="Connect Claude Code or Codex" onClose={() => setConnecting(false)} />
            <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pb-8">
              <Suspense fallback={null}>
                <ConnectPanel project={firstProjectOf(home.data)} />
              </Suspense>
            </div>
          </div>
        )}
      </Sheet>
    </WorkspaceShell>
  )
}

function Widget({
  title,
  count,
  action,
  children,
  className,
}: {
  title: string
  count?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section aria-label={title} className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-center gap-2">
        <h2 className="font-serif text-lg">{title}</h2>
        {count}
        <span className="ml-auto">{action}</span>
      </div>
      {children}
    </section>
  )
}

const Retry = ({ what, onRetry }: { what: string; onRetry: () => void }) => (
  <div className="flex items-center gap-3 rounded-card border border-line bg-surface p-4">
    <ErrorText>Couldn't load {what}.</ErrorText>
    <Button variant="secondary" size="sm" onClick={onRetry}>
      Retry
    </Button>
  </div>
)

const Skeleton = ({ className }: { className?: string }) => (
  <div className={cn("animate-pulse rounded-card bg-line-soft", className)} />
)

/** The project the Connect step imports: the first active one (by name), or none yet. */
const firstProjectOf = (
  home: HomeData | undefined,
): {
  readonly id: string
  readonly slug: string
  readonly name: string
  readonly onboarded: boolean
} | null => {
  const p = sortProjects(home?.projects ?? []).shown.find((x) => x.status === "active")
  return p ? { id: p.id, slug: p.slug, name: p.name, onboarded: p.live?.onboarded === true } : null
}

/**
 * What keeps the leads or the work from moving, above the projects (§4.2); hidden when nothing does. A fix the
 * checklist above already offers (Pair a runner, Add a key) shows once, there (UX pass phase 2).
 */
function HomeStrip({ home, checklistShown }: { home: HomeData; checklistShown: boolean }) {
  const { workspace } = workspaceRoute.useRouteContext()
  const apps = useQuery(connectedAppsQuery)
  // polish 2026-10-23 §5: a paired runner that sleeps reads "Jannik's runner is offline", per viewer
  // decision 0166 §1.1: the Platform Runner (with a subscription) starts on demand: it counts as online here
  const runners = withPlatformAwake(
    useQuery(runnersQuery(workspace.id)).data,
    useQuery({ ...platformQuery(workspace.id), refetchInterval: false }).data,
  )
  const people = useQuery(membersQuery(workspace.id)).data?.members
  const reachFacts = useHomeReach(workspace.id, home.me.userId)
  const platformRunnerId = useQuery({ ...platformQuery(workspace.id), refetchInterval: false }).data?.runnerId
  const { problems, leadName, waiting } = homeSetup(
    home,
    runners,
    (p) => homeReach(p, reachFacts),
    platformRunnerId ?? null,
  )
  const viewer = useMemo(
    () =>
      setupViewerOf(
        home.me.userId,
        runners ?? [],
        (people ?? []).map((m) => ({ userId: m.userId, displayName: m.user.name })),
      ),
    [home.me.userId, runners, people],
  )
  const open = checklistShown
    ? checklist(home, workspace.role, apps.data ? apps.data.length : null).filter((i) => !i.done)
    : []
  // decision 0130: a member joined a team that runs already; pairing and keys are the founders' (the line still says
  // what keeps the lead)
  const member = workspace.role === "member"
  const skip = [
    ...(member || open.some((i) => i.key === "runner") ? (["pair_runner"] as const) : []),
    ...(member || open.some((i) => i.key === "key") ? (["add_key"] as const) : []),
  ]
  return (
    <SetupStrip
      problems={problems}
      leadName={leadName}
      waiting={waiting}
      variant="page"
      workspaceRole={workspace.role}
      workspaceId={workspace.id}
      className="mb-6 border-y border-rule"
      skipFixes={skip}
      viewer={viewer}
    />
  )
}

function Checklist({
  home,
  onHide,
  onNewProject,
  onConnect,
}: {
  home: HomeData
  onHide: () => void
  onNewProject: () => void
  onConnect: () => void
}) {
  const { workspace } = workspaceRoute.useRouteContext()
  const member = workspace.role === "member"
  // members have no Connect step (decision 0130): their checklist never asks for the apps
  const apps = useQuery({ ...connectedAppsQuery, enabled: !member })
  const installed = useInstallState().installed
  // onboarding 2026-10-23 §5, §6: the phone step is done on a registered push device; the subscription step follows
  // the Platform Runner's subscriptions (members only)
  const push = useQuery({ ...pushStatusQuery, enabled: member })
  // decision 0166 §1.2: everyone's step 1 is their subscription; "Run on your own machine" ticks only when one of their
  // runners can run their lead
  const platform = useQuery({ ...platformQuery(workspace.id), refetchInterval: false })
  const runnerRows = useQuery(runnersQuery(workspace.id)).data
  const items = checklist(home, workspace.role, apps.data ? apps.data.length : null, {
    installed,
    pushDevices: push.data ? push.data.devices.length : null,
    subscriptions: subscriptionCounts(platform.data, home.me.userId),
    platformReady: platform.data ? platform.data.configured && platform.data.ownerUserId !== null : null,
    pairedRunnerReady: pairedRunnerReady(
      runnerRows
        ? runnerRows.map((r) => ({
            runnerId: r.runnerId,
            ownerUserId: r.ownerUserId,
            status: r.status,
            profiles: (parseManifest(r.manifest)?.profiles ?? []).map((p) => ({
              agent: p.agent,
              enabled: p.enabled,
              loggedIn: p.account.loggedIn,
            })),
          }))
        : null,
      home.me.userId,
      platform.data?.runnerId ?? null,
    ),
  })
  const progress = checklistProgress(items)
  // onboarding week A2: once the required steps are done, the phone step stays alone until a push device exists
  const after = progress.complete
    ? stepsAfterSetup(items, {
        pushDevices: push.data ? push.data.devices.length : null,
        pushEnabled: push.data?.enabled ?? null,
      })
    : items
  if (after.length === 0) return null
  const params = { workspaceId: workspace.id }
  const firstProject = home.projects.find((p) => p.status === "active")
  /** An open item is its own link (or button), so the label and the action are one thing. */
  const action = (key: string, label: ReactNode): ReactNode => {
    const cls = "tap font-semibold"
    switch (key) {
      case "runner":
        return (
          <Link to="/w/$workspaceId/settings/runners/new" params={params} className={cls}>
            {label} →
          </Link>
        )
      case "connect":
        return (
          <button type="button" className={cn(cls, "link")} onClick={onConnect}>
            {label} →
          </button>
        )
      case "project":
        return (
          <button type="button" className={cn(cls, "link")} onClick={onNewProject}>
            {label} →
          </button>
        )
      case "repo":
        return firstProject ? (
          <Link
            to="/w/$workspaceId/p/$projectId/settings/repos"
            params={{ ...params, projectId: firstProject.id }}
            className={cls}
          >
            {label} →
          </Link>
        ) : (
          label
        )
      case "invite":
        return (
          <Link to="/w/$workspaceId/settings/members" params={params} search={{ invite: 1 }} className={cls}>
            {label} →
          </Link>
        )
      case "key":
      case "subscription":
        return (
          <Link to="/w/$workspaceId/settings/models" params={params} className={cls}>
            {label} →
          </Link>
        )
      case "hi": {
        const projectId = items.find((i) => i.key === "hi")?.projectId
        return projectId ? (
          <Link
            to="/w/$workspaceId/p/$projectId"
            params={{ ...params, projectId }}
            search={{ view: "chat" }}
            className={cls}
          >
            {label} →
          </Link>
        ) : (
          label
        )
      }
      case "phone":
        return (
          <Link to="/w/$workspaceId/account/notifications" params={params} className={cls}>
            {label} →
          </Link>
        )
      default:
        return label
    }
  }
  return (
    <section aria-label="Get set up" className="mb-6 rounded-card border border-line bg-surface p-5">
      <div className="flex items-center gap-2">
        <h2 className="font-serif text-lg">{progress.complete ? "One more thing" : "Get set up"}</h2>
        {!progress.complete && (
          <span className="mono-data text-muted">
            · {progress.done} of {progress.total}
          </span>
        )}
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onHide}>
          Hide
        </Button>
      </div>
      <p className="mt-1 text-sm text-muted">
        {progress.complete
          ? "Get a notification on your phone when someone replies or something needs you."
          : member
            ? "The lead plans the work with you and asks you what only you can answer."
            : "Your Claude or Codex subscription runs the lead on the Platform Runner; no machine of yours needed. Start at the top."}
      </p>
      <ol className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        {after.map((item) => (
          <li key={item.key} className={cn("flex items-center gap-2", item.done && "text-muted")}>
            <span aria-hidden="true" className={item.done ? "text-ok" : "text-muted"}>
              {item.done ? "✓" : "○"}
            </span>
            <span>
              {item.optional && <span className="text-muted">Optional: </span>}
              {item.done ? item.label : action(item.key, item.label)}
              {item.hint && <span className="text-xs text-muted"> ({item.hint})</span>}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * Decision 0098: each lead's newest brief (a week at most), newest first. Refs open their page in that project; what
 * the lead needs from you is your Needs-you count there, one link (the cards themselves are not retyped).
 */
function Briefs({ home }: { home: HomeData }) {
  const { workspace } = workspaceRoute.useRouteContext()
  const briefs = homeBriefs(home, Date.now())
  if (briefs.length === 0) return null
  return (
    <Widget title="Briefs" className="mb-8">
      <ul className="flex flex-col gap-3">
        {briefs.map(({ project, brief, needsYou }) => {
          return (
            <li key={project.id}>
              <article
                aria-label={`Brief from ${brief.name}, ${project.name}`}
                className="flex flex-col gap-2 rounded-card border border-line bg-surface p-5"
              >
                <header className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <Avatar label={(brief.name[0] ?? "?").toUpperCase()} solid size={24} />
                  <Link
                    to="/w/$workspaceId/p/$projectId/agents/$handle"
                    params={{ workspaceId: workspace.id, projectId: project.id, handle: brief.handle }}
                    className="font-semibold text-ink"
                  >
                    {brief.name}
                  </Link>
                  <span className="text-muted">on</span>
                  <Link
                    to="/w/$workspaceId/p/$projectId"
                    params={{ workspaceId: workspace.id, projectId: project.id }}
                    className="font-semibold text-ink"
                  >
                    {project.name}
                  </Link>
                  <span className="mono-data ml-auto text-muted">
                    {whenText(brief.at, Date.now())}
                    {brief.sections ? " · updated live" : ""}
                  </span>
                </header>
                {/* decision 0144: the sections live (asks with their real answers), refs as chips with a preview */}
                <BriefView
                  body={brief.body}
                  sections={brief.sections}
                  workspaceId={workspace.id}
                  projectId={project.id}
                  leadName={brief.name}
                />
                {needsYou > 0 && (
                  <Link
                    to="/w/$workspaceId/p/$projectId"
                    params={{ workspaceId: workspace.id, projectId: project.id }}
                    search={{ view: "needs" }}
                    className="self-start text-sm font-semibold link"
                  >
                    {needsYou} {needsYou === 1 ? "thing is" : "things are"} waiting on you →
                  </Link>
                )}
              </article>
            </li>
          )
        })}
      </ul>
    </Widget>
  )
}

function ProjectsWidget({
  home,
  onNewProject,
}: {
  home: ReturnType<typeof useQuery<HomeData>>
  onNewProject: () => void
}) {
  const [showArchived, setShowArchived] = useState(false)
  const projects = home.data?.projects ?? []
  const { shown, archived } = sortProjects(projects)
  return (
    <Widget title="Projects">
      {home.isPending && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      )}
      {home.isError && !home.data && <Retry what="projects" onRetry={() => home.refetch()} />}
      {home.data && noProjectsYet(home.data) && (
        <p className="text-sm text-ink-2">{noProjectsText(home.data)}</p>
      )}
      {home.data && (
        <ul className="grid gap-4 sm:grid-cols-2">
          {[...shown, ...(showArchived ? archived : [])].map((p) => (
            <li key={p.id}>
              <ProjectCard project={p} home={home.data} />
            </li>
          ))}
          {showStartCard(home.data) && (
            <li>
              <button
                type="button"
                onClick={onNewProject}
                className="flex h-full min-h-32 w-full flex-col justify-center gap-2 rounded-card border border-dashed border-line-control p-5 text-left hover:border-brand/50"
              >
                <span className="font-semibold text-ink-2">＋ {WORDS.startProject}</span>
                <span className="text-sm text-muted">
                  {projects.length === 0
                    ? "Name it and its lead, pick its repo, then say what you want to build."
                    : "Name it and its lead, then say what you want to build."}
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
      {archived.length > 0 && (
        <button type="button" className="self-start text-sm link" onClick={() => setShowArchived((v) => !v)}>
          {showArchived ? "Hide archived" : `Show ${archived.length} archived`}
        </button>
      )}
    </Widget>
  )
}

/**
 * Decision 0129: a plain member who is in no project (an invite that named none): Home says to ask to be added. Owners
 * and admins see every project, so for them an empty list just means there is none yet.
 */
export const noProjectsYet = (home: HomeData): boolean =>
  home.me.role === "member" && !home.projects.some((p) => p.access !== "reader")

/**
 * Polish 2026-10-23 §4: what Home says to a member in no project. When a project open to the workspace is listed, they
 * can join it themselves (and Get set up says "Join … and say hi"), so it never says to ask an admin.
 */
export const noProjectsText = (home: HomeData): string =>
  home.projects.some((p) => p.access === "reader" && p.status === "active")
    ? "You're not in a project yet. Join one below to work in it, or start your own."
    : "You're not in any project yet. Ask an admin of a project to add you, or start one."

function ProjectCard({ project, home }: { project: ProjectSummary; home: HomeData }) {
  const { workspace } = workspaceRoute.useRouteContext()
  const platform = useQuery({ ...platformQuery(workspace.id), refetchInterval: false }).data
  const runners = withPlatformAwake(useQuery(runnersQuery(workspace.id)).data, platform) ?? []
  // decision 0168: the viewer's reach, so the card says "can't work" exactly when the chat does
  const reachFacts = useHomeReach(workspace.id, home.me.userId)
  const runnerName = (id: string) =>
    runners.find((r) => r.runnerId === id)?.name ?? `runner ${id.slice(4, 10)}`
  const live = project.live
  const counts = live ? countsLine(live.counts) : ""
  return (
    <Link
      to="/w/$workspaceId/p/$projectId"
      params={{ workspaceId: workspace.id, projectId: project.id }}
      className={cn(
        "tap flex h-full flex-col gap-3 rounded-card border border-line bg-surface p-5 text-ink no-underline transition-colors hover:border-brand/40 hover:text-ink",
        project.status === "archived" && "opacity-70",
      )}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex size-7 shrink-0 items-center justify-center rounded-control bg-fill text-[13px] font-semibold"
        >
          {project.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="type-card min-w-0 truncate">{project.name}</span>
        <span className="ml-auto flex shrink-0 gap-1.5">
          {live && live.needsYou > 0 && <Chip tone="needs">Needs you {live.needsYou}</Chip>}
          {/* decision 0129: private projects, and workspace projects you only read (Join inside) */}
          {project.visibility === "private" && <Chip tone="outline">Private</Chip>}
          {project.access === "reader" && <Chip tone="outline">Not joined</Chip>}
          {project.status !== "active" && (
            <Chip tone="outline">{project.status === "paused" ? "Paused" : "Archived"}</Chip>
          )}
        </span>
      </div>
      {live ? (
        <>
          {live.goals.length === 0 ? (
            <p className="text-sm text-muted">{goallessLine(live)}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {live.goals.map((g) => {
                const p = goalProgress(g)
                return (
                  <li key={g.id} className="flex flex-col gap-1">
                    <span className="flex items-baseline gap-2 text-sm">
                      <span className="mono-data text-muted">{g.ref}</span>
                      <span className="min-w-0 flex-1 truncate font-medium">{g.title}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-[12px] text-ink-2">{p.text}</span>
                      <span className="h-1 flex-1 rounded-sm bg-line">
                        <span
                          className="block h-1 rounded-sm bg-brand"
                          style={{ width: `${Math.round(p.ratio * 100)}%` }}
                        />
                      </span>
                      {p.due && (
                        <span className="mono-data text-muted">
                          {new Date(p.due).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                        </span>
                      )}
                    </span>
                  </li>
                )
              })}
              {live.moreGoals > 0 && (
                <li className="text-xs text-muted">
                  +{live.moreGoals} more goal{live.moreGoals === 1 ? "" : "s"}
                </li>
              )}
            </ul>
          )}
          {counts && <p className="mono-data text-muted">{counts}</p>}
          {live.lead && (
            <p className="text-xs text-ink-2" title={leadWhere(live.lead.mind, runnerName)}>
              {leadLine(
                live.lead,
                projectProblems(
                  home,
                  project,
                  runners,
                  homeReach(project, reachFacts),
                  platform?.runnerId ?? null,
                ).some(blocksLead),
              )}
            </p>
          )}
        </>
      ) : (
        project.status !== "archived" && <p className="mono-data text-muted">Couldn't load live numbers</p>
      )}
      <span className="mt-auto text-sm font-semibold link">
        {project.access === "reader" ? "Open to read or join →" : "Open →"}
      </span>
    </Link>
  )
}
